/*
 * HERCULE — grille produits à onglets (refonte perf 2026)
 * Pilote sections/hs-product-tabs.liquid.
 *
 * Principe : un seul onglet est rendu par le serveur. Les autres, ainsi que
 * les produits suivants d'un onglet, sont chargés via l'endpoint
 * hs-product-grid-api (convention `api-*` de Broadcast, comme le fait déjà
 * hercule-collection-grid-api pour l'ancienne section).
 *
 * Dépendances au thème — markup uniquement, aucune fonction interne :
 *  - <quick-add-product> : les cartes injectées sont « upgradées » par le
 *    navigateur à l'insertion, l'ajout au panier et l'ouverture du drawer
 *    restent donc ceux de Broadcast (événements theme:cart:add /
 *    theme:cart-drawer:open). Rien n'est réimplémenté ici.
 *  - <hover-images> : cloné depuis un <template> au premier survol.
 *  - classes AOS (aos-animate / aos-no-transition) : même séquence que
 *    tabs-component pour rejouer l'apparition au changement d'onglet.
 */

(function () {
  'use strict';

  if (window.customElements.get('hs-product-tabs')) return;

  var SEL = {
    link: '.tab-link',
    panel: '.tab-content',
    grid: '.grid',
    gridOuter: '.grid-outer',
    item: '.grid-item',
    productItem: '.product-item',
    hoverTemplate: 'template[data-hs-hover]',
    bg: '.product-item__bg',
    seeAll: '.hercule-see-all-slide',
    loadMore: '.hercule-load-more',
  };

  var CLASS = {
    current: 'current',
    loading: 'hs-tabs--loading',
    aosAnimate: 'aos-animate',
    aosNoTransition: 'aos-no-transition',
  };

  class HsProductTabs extends HTMLElement {
    connectedCallback() {
      if (this.hsInitialized) return;
      this.hsInitialized = true;

      this.apiSection = this.dataset.apiSection || 'hs-product-grid-api';
      this.pageSize = parseInt(this.dataset.pageSize, 10) || 12;
      this.mobileMax = parseInt(this.dataset.mobileMax, 10) || 6;
      this.step = parseInt(this.dataset.loadMoreStep, 10) || 12;
      this.initialVisible = parseInt(this.dataset.initialVisible, 10) || this.step;

      this.mqMobile = window.matchMedia('(max-width: 749px)');
      this.mqHover = window.matchMedia('(hover: hover)');

      this.links = Array.prototype.slice.call(this.querySelectorAll(SEL.link));
      this.panels = Array.prototype.slice.call(this.querySelectorAll(SEL.panel));

      // Cache mémoire des fragments HTML : `${handle}:${page}` → HTML.
      // Un onglet déjà ouvert n'est jamais rechargé.
      this.cache = new Map();

      this.panels.forEach(this.setupPanel, this);

      // Un seul listener délégué pour les onglets ET « Charger plus ».
      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));

      // Images de survol : clonées à la demande. Sur mobile le CSS masque le
      // slider de survol (.grid--mobile-slider .product-item__bg__under) —
      // inutile de cloner quoi que ce soit.
      if (this.mqHover.matches) {
        this.addEventListener('mouseover', this.onIntent.bind(this));
      } else if (!this.mqMobile.matches) {
        this.addEventListener('touchstart', this.onIntent.bind(this), {passive: true});
      }

      this.onBreakpoint = this.onBreakpoint.bind(this);
      this.mqMobile.addEventListener('change', this.onBreakpoint);
    }

    disconnectedCallback() {
      this.mqMobile.removeEventListener('change', this.onBreakpoint);
    }

    /* ---------------------------------------------------------------- état */

    setupPanel(panel) {
      var url = (panel.dataset.collectionUrl || '').split('?')[0];
      // Produits BRUTS déjà consommés par la boucle Liquid, pas nombre de
      // cartes : un produit taggé « hide » occupe une place dans la collection
      // sans être rendu. Compter les cartes décalerait la pagination.
      var consumed = parseInt(panel.dataset.serverLimit, 10) || 0;
      var total = parseInt(panel.dataset.productsCount, 10) || 0;

      var state = {
        url: url,
        total: total,
        consumed: consumed,
        nextPage: Math.floor(consumed / this.pageSize) + 1,
        exhausted: !url || (consumed > 0 && consumed >= total),
        loading: false,
        loaded: !panel.hasAttribute('data-hs-pending'),
        seen: Object.create(null),
        visible: this.initialVisible,
        grid: panel.querySelector(SEL.grid),
        seeAll: null,
        button: null,
      };

      panel.hsState = state;

      this.registerExisting(panel);
      this.buildSeeAll(panel);
      this.buildLoadMore(panel);
      this.applyVisibility(panel);
    }

    registerExisting(panel) {
      var state = panel.hsState;
      panel.querySelectorAll(SEL.item).forEach(function (item) {
        var key = HsProductTabs.keyOf(item);
        if (key) state.seen[key] = true;
      });
    }

    static keyOf(item) {
      return (item.dataset.url || item.id || '').split('?')[0];
    }

    items(panel) {
      return panel.querySelectorAll(SEL.gridOuter + ' ' + SEL.item);
    }

    /* ------------------------------------------------------------- rendus */

    buildSeeAll(panel) {
      var state = panel.hsState;
      if (!state.url || !state.grid) return;

      // Dernière slide du carrousel mobile. Sa visibilité est pilotée en CSS
      // (masquée en desktop) — même rendu que « Charger plus ».
      var card = document.createElement('a');
      card.className = 'btn btn--text btn--black hercule-see-all-slide';
      card.href = panel.dataset.collectionUrl;
      card.innerHTML = '<span>' + HsProductTabs.escape(panel.dataset.seeAllLabel || 'Voir tout') + '</span>';
      state.grid.appendChild(card);
      state.seeAll = card;
    }

    buildLoadMore(panel) {
      var state = panel.hsState;
      var outer = panel.querySelector(SEL.gridOuter);
      if (!outer) return;

      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'btn btn--text btn--black hercule-load-more';
      // <span> requis pour le survol natif Broadcast des .btn--text
      button.innerHTML = '<span>' + HsProductTabs.escape(panel.dataset.loadMoreLabel || 'Charger plus') + '</span>';
      outer.after(button);
      state.button = button;
    }

    static escape(value) {
      var div = document.createElement('div');
      div.textContent = value;
      return div.innerHTML;
    }

    /* --------------------------------------------------------- visibilité */

    applyVisibility(panel) {
      var state = panel.hsState;
      var list = this.items(panel);
      var mobile = this.mqMobile.matches;
      var cap = mobile ? this.mobileMax : state.visible;

      list.forEach(function (item, i) {
        item.style.display = i < cap ? '' : 'none';
      });

      var done = state.visible >= list.length && state.exhausted;
      if (state.button) {
        // Mobile : pas de « Charger plus » — le cap + la carte « Voir tout »
        // tiennent lieu de fin de liste (règle CSS existante).
        state.button.style.display = mobile || done || !state.loaded ? 'none' : '';
      }
    }

    onBreakpoint() {
      this.panels.forEach(this.applyVisibility, this);
    }

    /* ------------------------------------------------------------ réseau */

    fetchPage(panel, page) {
      var state = panel.hsState;
      var key = panel.dataset.collectionHandle + ':' + page;

      if (this.cache.has(key)) return Promise.resolve(this.cache.get(key));

      var url = state.url + '?section_id=' + encodeURIComponent(this.apiSection) + '&page=' + page;
      var cache = this.cache;

      return fetch(url)
        .then(function (response) {
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return response.text();
        })
        .then(function (html) {
          cache.set(key, html);
          return html;
        });
    }

    // Charge la page suivante et l'insère. Résout à false si rien de plus.
    loadNext(panel) {
      var state = panel.hsState;
      if (state.loading || state.exhausted) return Promise.resolve(false);

      state.loading = true;
      if (state.button) state.button.classList.add('is-loading');

      var self = this;
      var page = state.nextPage;

      return this.fetchPage(panel, page)
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var added = self.insertCards(panel, doc);
          state.nextPage = page + 1;
          // Fin de collection : on se fie au marqueur paginate.next rendu par
          // la section API, pas au nombre de cartes reçues (une page peut en
          // renvoyer moins que la taille de page si des produits sont taggés
          // « hide »).
          if (!doc.querySelector('[data-hs-has-next]')) state.exhausted = true;
          return added > 0 || !state.exhausted;
        })
        .catch(function () {
          state.exhausted = true;
          return false;
        })
        .then(function (result) {
          state.loading = false;
          if (state.button) state.button.classList.remove('is-loading');
          return result;
        });
    }

    insertCards(panel, doc) {
      var state = panel.hsState;
      if (!state.grid) return 0;

      var added = 0;
      // Garde-fou anti-doublon : la fenêtre rendue par le serveur n'est pas
      // forcément un multiple de la taille de page, la première page AJAX
      // recouvre donc la fin du rendu serveur.
      doc.querySelectorAll(SEL.item).forEach(function (item) {
        var key = HsProductTabs.keyOf(item);
        if (key && state.seen[key]) return;
        if (key) state.seen[key] = true;
        item.style.display = 'none';
        state.grid.insertBefore(item, state.seeAll);
        added += 1;
      });

      return added;
    }

    // Enchaîne les pages jusqu'à atteindre `target` cartes (une page peut
    // n'en apporter que quelques-unes après dédoublonnage).
    fillTo(panel, target) {
      var self = this;
      var state = panel.hsState;

      if (this.items(panel).length >= target || state.exhausted) {
        return Promise.resolve();
      }

      return this.loadNext(panel).then(function (progressed) {
        if (!progressed) return;
        return self.fillTo(panel, target);
      });
    }

    /* ------------------------------------------------------------ onglets */

    onClick(event) {
      var link = event.target.closest(SEL.link);
      if (link && this.contains(link)) {
        this.activate(link);
        return;
      }

      var button = event.target.closest(SEL.loadMore);
      if (button && this.contains(button)) {
        this.onLoadMore(button.closest(SEL.panel));
      }
    }

    onLoadMore(panel) {
      if (!panel) return;
      var state = panel.hsState;
      // Un clic pendant un fetch relancerait fillTo() alors que loadNext()
      // rend la main immédiatement → boucle de micro-tâches.
      if (state.loading) return;

      state.visible += this.step;
      var self = this;

      if (this.items(panel).length >= state.visible) {
        this.applyVisibility(panel);
      } else {
        this.fillTo(panel, state.visible).then(function () {
          self.applyVisibility(panel);
        });
      }
    }

    onKeydown(event) {
      var link = event.target.closest(SEL.link);
      if (!link || !this.contains(link)) return;

      var index = this.links.indexOf(link);
      var next = null;

      switch (event.key) {
        case 'ArrowRight':
          next = this.links[index + 1] || this.links[0];
          break;
        case 'ArrowLeft':
          next = this.links[index - 1] || this.links[this.links.length - 1];
          break;
        case 'Home':
          next = this.links[0];
          break;
        case 'End':
          next = this.links[this.links.length - 1];
          break;
        case 'Enter':
        case ' ':
          event.preventDefault();
          this.activate(link);
          return;
        default:
          return;
      }

      event.preventDefault();
      next.focus();
      this.activate(next);
    }

    activate(link) {
      if (link.classList.contains(CLASS.current)) return;

      var panel = this.querySelector('#' + CSS.escape(link.getAttribute('aria-controls')));
      if (!panel) return;

      this.links.forEach(function (element) {
        var isCurrent = element === link;
        element.classList.toggle(CLASS.current, isCurrent);
        element.setAttribute('aria-selected', isCurrent ? 'true' : 'false');
        element.setAttribute('tabindex', isCurrent ? '0' : '-1');
      });

      this.panels.forEach(function (element) {
        element.classList.toggle(CLASS.current, element === panel);
      });

      var self = this;
      this.ensureLoaded(panel).then(function () {
        self.applyVisibility(panel);
        self.animateItems(panel);
      });
    }

    ensureLoaded(panel) {
      var state = panel.hsState;
      if (state.loaded) return Promise.resolve();

      var self = this;
      if (state.grid) state.grid.classList.add(CLASS.loading);

      return this.fillTo(panel, this.initialVisible)
        .then(function () {
          state.loaded = true;
          panel.removeAttribute('data-hs-pending');
        })
        .then(function () {
          if (state.grid) state.grid.classList.remove(CLASS.loading);
          self.animateItems(panel);
        });
    }

    // Même séquence que tabs-component : on retire l'état animé, on coupe la
    // transition le temps d'un frame, puis on rejoue.
    animateItems(panel) {
      panel.querySelectorAll('[data-aos]').forEach(function (item) {
        item.classList.remove(CLASS.aosAnimate);
        item.classList.add(CLASS.aosNoTransition);

        requestAnimationFrame(function () {
          item.classList.remove(CLASS.aosNoTransition);
          requestAnimationFrame(function () {
            item.classList.add(CLASS.aosAnimate);
          });
        });
      });
    }

    /* -------------------------------------------------- images de survol */

    onIntent(event) {
      var item = event.target.closest(SEL.productItem);
      if (!item || !this.contains(item) || item.hsHoverDone) return;
      item.hsHoverDone = true;

      var template = item.querySelector(SEL.hoverTemplate);
      if (!template) return;

      var bg = item.querySelector(SEL.bg);
      if (!bg) return;

      // Le contenu d'un <template> n'appartient pas au document : ni nœuds
      // comptés, ni images téléchargées avant ce clonage.
      bg.after(template.content.cloneNode(true));
      template.remove();
    }
  }

  window.customElements.define('hs-product-tabs', HsProductTabs);
})();
